import { AD_SLOTS } from "../../config/adSlots";
import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Forum,
  fetchForums,
  createForum,
  fetchTopicCount,
  deleteForum,
  editForum,
} from "../../services/forumService";
import { auth } from "../../services/firebaseService";
import AdBanner from "../ads/AdBanner";
import { MoreVertical, Edit3, Trash2, X } from "lucide-react";

export default function ForumList() {
  const user = auth.currentUser;
  const navigate = useNavigate();
  const [forums, setForums] = useState<Forum[]>([]);
  const [topicCounts, setTopicCounts] = useState<Record<string, number>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [newName, setNewName] = useState("");
  const [newTags, setNewTags] = useState("");
  const [menuOpenFor, setMenuOpenFor] = useState<string | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Forum | null>(null);
  const [editName, setEditName] = useState("");
  const [editTags, setEditTags] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const forumsByTag = useRef<Record<string, Forum[]>>({});

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const fs = await fetchForums();
      setForums(fs);
      const pairs = await Promise.all(
        fs.map((f) =>
          fetchTopicCount(f.id).then((c) => [f.id, c] as [string, number])
        )
      );
      setTopicCounts(Object.fromEntries(pairs));
    } catch (error) {
      console.error("Error loading forums:", error);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim() || !user || isSubmitting) return;

    try {
      setIsSubmitting(true);
      const tagsArray = newTags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);

      await createForum(newName.trim(), user.uid, tagsArray);
      setNewName("");
      setNewTags("");
      await loadAll();
    } catch (error) {
      console.error("Error creating forum:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteId) return;
    await deleteForum(confirmDeleteId);
    setConfirmDeleteId(null);
    await loadAll();
  };

  const openEditDialog = (forum: Forum) => {
    setEditTarget(forum);
    setEditName(forum.name);
    setEditTags(forum.tags.join(", "));
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    setEditDialogOpen(false);
    setEditTarget(null);
    setEditName("");
    setEditTags("");
  };

  const handleEdit = async () => {
    if (!editTarget || !editName.trim()) return;

    const updatedTags = editTags
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    await editForum(editTarget.id, {
      name: editName.trim(),
      tags: updatedTags,
    });

    closeEditDialog();
    await loadAll();
  };

  const filtered = forums.filter(
    (f) =>
      f.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.tags.some((t) => t.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Create a fresh grouped object each time to avoid accumulation
  const groupForumsByTag = () => {
    const grouped: Record<string, Forum[]> = {};

    filtered.forEach((forum) => {
      // If the forum has no tags, add it to "General"
      const tagList = forum.tags.length ? forum.tags : ["General"];

      tagList.forEach((tag) => {
        if (!grouped[tag]) {
          grouped[tag] = [];
        }

        // Only add the forum if it doesn't already exist in this tag group
        const alreadyExists = grouped[tag].some((f) => f.id === forum.id);
        if (!alreadyExists) {
          grouped[tag].push(forum);
        }
      });
    });

    return grouped;
  };

  // Get a fresh grouping on each render
  const grouped = groupForumsByTag();

  if (!user) {
    return (
      <div className="p-6 text-center text-white">
        Please{" "}
        <Link to="/" className="text-blue-400 underline">
          sign in
        </Link>{" "}
        to view forums.
      </div>
    );
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !isSubmitting && newName.trim()) {
      handleCreate();
    }
  };

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-3xl font-bold text-white flex items-center gap-2">
        🗨️ Forums
      </h1>

      {/* Create panel */}
      <div className="flex flex-col md:flex-row gap-2">
        <input
          type="text"
          placeholder="Forum name"
          className="flex-1 px-4 py-2 bg-gray-700 text-white rounded"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <input
          type="text"
          placeholder="Tags (comma separated)"
          className="flex-1 px-4 py-2 bg-gray-700 text-white rounded"
          value={newTags}
          onChange={(e) => setNewTags(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <button
          onClick={handleCreate}
          disabled={isSubmitting || !newName.trim()}
          className={`px-4 py-2 ${
            isSubmitting || !newName.trim()
              ? "bg-gray-500 cursor-not-allowed"
              : "bg-green-600 hover:bg-green-700 cursor-pointer"
          } text-white rounded flex items-center justify-center min-w-20`}
        >
          {isSubmitting ? "Creating..." : "Create"}
        </button>
      </div>

      {/* Search */}
      <input
        type="text"
        className="w-full px-4 py-2 bg-gray-700 text-white rounded"
        placeholder="Search forums by name or tag…"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {/* Banner Ad */}
      <div className="my-6">
        <AdBanner
          adSlot={AD_SLOTS.forumList}
          adFormat="horizontal"
          fullWidthResponsive={true}
          style={{ maxWidth: '728px', margin: '0 auto' }}
        />
      </div>

      {/* Grouped listing */}
      {Object.entries(grouped).map(([tag, items]) => (
        <div key={tag} className="space-y-4">
          <h2 className="text-2xl font-semibold text-gray-200"># {tag}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((f) => (
              <div key={`${tag}-${f.id}`} className="relative">
                <Link
                  to={`/forums/${f.id}`}
                  className="flex flex-col justify-between p-4 bg-gray-700 hover:bg-gray-600 shadow-md rounded-lg transition"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xl">💬</span>
                    <h3 className="text-xl text-white font-semibold">
                      {f.name}
                    </h3>
                    <span className="ml-auto text-xs bg-gray-600 text-white px-2 py-1 rounded-full">
                      {topicCounts[f.id] ?? 0} topics
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {f.tags.map((t) => (
                      <span
                        key={t}
                        className="text-xs bg-blue-600 text-white px-2 py-1 rounded-full"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </Link>

                {/* Ellipsis menu for the creator */}
                {f.creatorId === user.uid && (
                  <>
                    <button
                      onClick={() =>
                        setMenuOpenFor(menuOpenFor === f.id ? null : f.id)
                      }
                      className="absolute bottom-2 right-2 p-1 text-white hover:text-blue-400"
                    >
                      <MoreVertical size={18} />
                    </button>

                    {menuOpenFor === f.id && (
                      <div className="absolute top-10 right-2 z-10 bg-gray-900 border border-gray-700 rounded shadow-md">
                        <button
                          onClick={() => {
                            setMenuOpenFor(null);
                            openEditDialog(f);
                          }}
                          className="flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-700"
                        >
                          <Edit3 size={14} /> Edit
                        </button>
                        <button
                          onClick={() => {
                            setConfirmDeleteId(f.id);
                            setMenuOpenFor(null);
                          }}
                          className="flex items-center gap-2 px-4 py-2 text-red-400 hover:bg-gray-700"
                        >
                          <Trash2 size={14} /> Delete
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {filtered.length === 0 && (
        <div className="text-center text-gray-400">
          No forums match your search.
        </div>
      )}

      {/* Edit Dialog */}
      {editDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-gray-800 p-6 rounded-lg max-w-md w-full text-white">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Edit Forum</h2>
              <button
                onClick={closeEditDialog}
                className="text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Forum Name
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded"
                  placeholder="Forum name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Tags (comma separated)
                </label>
                <input
                  type="text"
                  value={editTags}
                  onChange={(e) => setEditTags(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded"
                  placeholder="Tags"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={closeEditDialog}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleEdit}
                disabled={!editName.trim()}
                className={`px-4 py-2 ${
                  !editName.trim()
                    ? "bg-gray-500"
                    : "bg-blue-600 hover:bg-blue-500"
                } text-white rounded`}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Deletion Modal */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60">
          <div className="bg-gray-800 p-6 rounded-lg max-w-sm w-full text-white">
            <h2 className="text-lg font-semibold mb-3">Delete this forum?</h2>
            <p className="mb-4 text-gray-300">This action cannot be undone.</p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="px-4 py-2 bg-gray-600 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
